import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

import type { StoreEvent } from './events.model';

/** Los eventos de un día del calendario, con la clave civil (`YYYY-MM-DD`) en Costa Rica. */
export interface EventDialogDay {
  readonly dayKey: string;
  readonly events: readonly StoreEvent[];
}

/**
 * Lo que pinta el diálogo. `fromDay` solo existe cuando el detalle se abrió
 * desde la vista día: es lo que hace aparecer «Volver» y adonde regresa
 * `back()`. Abierto desde fuera, el detalle no tiene a qué volver (D19).
 */
export type EventDialogView =
  | ({ readonly kind: 'day' } & EventDialogDay)
  | {
      readonly kind: 'detail';
      readonly event: StoreEvent;
      readonly fromDay: EventDialogDay | null;
    };

/**
 * Estado del diálogo de eventos, el único de la web (D19, D20).
 *
 * Servicio raíz y **ligero a propósito**: lo inyecta `App` para el
 * `@defer (when …)` que monta el diálogo, así que entra en el bundle inicial.
 * Solo depende de `@angular/core`, `@angular/common` y de tipos; el diálogo,
 * que es lo pesado, se carga perezoso la primera vez que se abre.
 *
 * Quien dispara no cablea nada: la tarjeta, la landing y el calendario llaman
 * aquí directamente, sin outputs que haya que propagar por cada padre (D21).
 */
@Injectable({ providedIn: 'root' })
export class EventDialogService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private readonly state = signal<EventDialogView | null>(null);

  readonly view = this.state.asReadonly();
  readonly isOpen = computed(() => this.state() !== null);

  /**
   * Elemento que abrió el diálogo, para devolverle el foco al cerrar (D23). Se
   * captura solo al pasar de cerrado a abierto: el detalle abierto desde la
   * vista día devuelve el foco al botón del día del calendario, no a una
   * tarjeta del propio diálogo que al cerrar ya no está en el DOM.
   */
  private trigger: HTMLElement | null = null;

  /**
   * Se cierra al empezar cualquier navegación (D28): es un overlay propio —la
   * web no usa Material ni CDK—, así que no hereda un `closeOnNavigation`. Sin
   * esto, el botón «atrás» del navegador cambiaría la página por debajo con el
   * diálogo aún abierto. El foco vuelve al disparador solo si sigue en el DOM.
   */
  constructor() {
    inject(Router)
      .events.pipe(filter((e) => e instanceof NavigationStart), takeUntilDestroyed())
      .subscribe(() => this.close());
  }

  /** Detalle de un evento. Desde la vista día, conserva el día para «Volver». */
  openEvent(event: StoreEvent): void {
    const current = this.state();
    if (!current) this.rememberTrigger();

    const fromDay =
      current?.kind === 'day' ? { dayKey: current.dayKey, events: current.events } : null;
    this.state.set({ kind: 'detail', event, fromDay });
  }

  /**
   * Eventos de un día. Con **exactamente uno**, abre su detalle directamente y
   * sin «Volver»: una lista de un solo elemento sería un paso en balde (D21).
   */
  openDay(dayKey: string, events: readonly StoreEvent[]): void {
    if (events.length === 0) return;
    if (!this.state()) this.rememberTrigger();

    this.state.set(
      events.length === 1
        ? { kind: 'detail', event: events[0], fromDay: null }
        : { kind: 'day', dayKey, events },
    );
  }

  /** Del detalle a la lista del día de la que se llegó. Sin día de origen, no hace nada. */
  back(): void {
    const current = this.state();
    if (current?.kind !== 'detail' || !current.fromDay) return;
    this.state.set({ kind: 'day', ...current.fromDay });
  }

  close(): void {
    this.state.set(null);
  }

  /** Entrega el disparador y lo olvida. Lo consume el diálogo al cerrarse. */
  takeTrigger(): HTMLElement | null {
    const trigger = this.trigger;
    this.trigger = null;
    return trigger;
  }

  private rememberTrigger(): void {
    // En el servidor no hay foco que devolver, y `document` es el de Domino.
    if (!isPlatformBrowser(this.platformId)) return;
    const active = this.document.activeElement;
    this.trigger = active instanceof HTMLElement ? active : null;
  }
}
